import type { SheetForm, SheetTextField } from '~/helpers/sessions/training-sheet'
import { useTranslation } from 'react-i18next'
import { Input } from '~/components/ui/input'
import { SHEET_LIMITS, SHEET_TEXT_FIELDS, sheetObjectives } from '~/helpers/sessions/training-sheet'

const AREA = 'rounded-md border border-input bg-transparent px-3 py-2 text-sm'

/**
 * La fiche formation d'un parcours (#319) : ce que l'apprenant doit savoir avant
 * et pendant la formation (Qualiopi, indicateurs 1, 5, 6). Repliée par défaut :
 * tout y est facultatif, et elle ne doit pas allonger la création d'un parcours
 * pour qui n'en a pas besoin.
 */
function TrainingSheetFields({ value, onChange }: { value: SheetForm, onChange: (value: SheetForm) => void }) {
  const { t } = useTranslation()
  const set = (field: keyof SheetForm) => (text: string) => onChange({ ...value, [field]: text })
  const hasObjectives = sheetObjectives(value).length > 0

  return (
    <details className="group rounded-lg border border-border p-4">
      <summary className="cursor-pointer text-sm font-medium">{t('sheet.title')}</summary>
      <div className="mt-4 flex flex-col gap-4">
        <p className="text-xs text-muted-foreground">{t('sheet.help')}</p>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="sheet-objectives">{t('sheet.objectives')}</label>
          <textarea id="sheet-objectives" rows={4} value={value.objectives} onChange={e => set('objectives')(e.target.value)} aria-describedby="sheet-objectives-help" className={AREA} />
          <p id="sheet-objectives-help" className="text-xs text-muted-foreground">{t('sheet.objectives.help', { count: SHEET_LIMITS.objectives })}</p>
        </div>
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={value.positioningEnabled && hasObjectives}
              disabled={!hasObjectives}
              onChange={e => onChange({ ...value, positioningEnabled: e.target.checked })}
              aria-describedby="sheet-positioning-help"
            />
            {t('sheet.positioning')}
          </label>
          <p id="sheet-positioning-help" className="text-xs text-muted-foreground">{t(hasObjectives ? 'sheet.positioning.help' : 'sheet.positioning.needsObjectives')}</p>
        </div>
        {SHEET_TEXT_FIELDS.map((field: SheetTextField) => (
          <div key={field} className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor={`sheet-${field}`}>{t(`sheet.${field}`)}</label>
            <textarea id={`sheet-${field}`} rows={2} maxLength={SHEET_LIMITS.text} value={value[field]} onChange={e => set(field)(e.target.value)} className={AREA} />
          </div>
        ))}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="sheet-contact">{t('sheet.contact')}</label>
          <Input id="sheet-contact" maxLength={SHEET_LIMITS.contact} value={value.contact} onChange={e => set('contact')(e.target.value)} />
        </div>
      </div>
    </details>
  )
}

export default TrainingSheetFields
